import { Divider } from "components/design/Divider";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import Modal from "components/DesignSystem/Modal/Modal";
import { Stepper } from "components/DesignSystem/Stepper/Stepper";
import { Label, TextInput } from "components/DesignSystem/TextInput/TextInput";
import { ArrowRight } from "components/icons/ArrowRight";
import { Cross } from "components/icons/Cross";
import { EditPencil } from "components/icons/EditPencil";
import { LoadingIcon } from "components/icons/LoadingIcon";
import { PriceInput } from "components/PriceInput/PriceInput";
import { TransactionsView } from "components/TransactionsView/TransactionsView";
import {
  CategoryIndent,
  coaOptions,
} from "components/Transaction/TransactionColumn";
import { datePeriod } from "constants/bookkeeping";
import { DISPLAY_DATE, MM_DD_YYYY, YYYY_MM_DD } from "constants/date";
import {
  AMOUNT_RANGE_ERROR,
  MAX_RULE_CONDITIONS,
  RULE_PRIORITY,
  RULE_PRIORITY_OPTIONS,
  RulePriorityType,
} from "constants/ruleEngine";
import dayjs from "dayjs";
import {
  Field,
  FieldProps,
  Form,
  Formik,
  FormikHelpers,
  useFormikContext,
} from "formik";
import { categorisationRuleSchema } from "formValidations/categorisationRuleSchema";
import { AnimatePresence, motion } from "framer-motion";
import { useChartOfAccounts } from "hooks/useChartOfAccounts";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { usePaginatedQuery } from "hooks/usePaginatedQuery";
import { useToast } from "hooks/useToast";
import { EmptyScreen } from "pages/Books/EmptyScreen";
import randomBytes from "randombytes";
import { ReactNode, useId, useMemo, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { PropsValue } from "react-select";
import {
  useApplyRuleMutation,
  useCreateRuleMutation,
  useGetAllConditionsQuery,
  useGetPrefilledRuleQuery,
  useLazyGetRuleTransactionsQuery,
} from "store/apis/ruleEngine";
import { useGetAllClassificationQuery } from "store/apis/classifications";
import { Transactions } from "types/Models/books";
import { RuleConditionRow, RulesData } from "types/Models/ruleEngine";
import { BackendError } from "types/utils/error";
import { ModalProps } from "types/utils/modal";
import { flattenTypes } from "utils/flattenCOA";
import { CategoryLabel } from "components/design/CategoryLabel";
import { useSelector } from "react-redux";
import { RootState } from "store/store";
import { SourceValue } from "./SourceValue";
import { VendorValue } from "./VendorValue";
import { useWindowSize } from "hooks/useWindowSize";
import { formatOrdinals } from "utils/formatOrdinals";
import { getAvailableRuleOperators } from "utils/getAvailableRuleOperators";
import { getAvailableRuleFields } from "utils/getAvailableRuleFields";
import { getInvalidRuleAmountRangeRows } from "utils/getInvalidRuleAmountRangeRows";
import MerchantSelector from "components/VendorBills/MerchantSelector";
import { ArrowDown } from "components/icons/ArrowDown";
import classNames from "classnames";
import { ClassificationAssignment } from "components/ClassificationAssignment/ClassificationAssignment";
import DateSelectBox from "components/design/dateSelectBox";
import { useGetMerchantByIdQuery } from "store/apis/merchant";
import { useCurrentGroup } from "hooks/useCurrentGroup";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { useModal } from "hooks/useModal";
import { ClassificationGroup } from "types/Models/classification";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { DiamondPurple } from "components/icons/DiamondPurple";
import { CLASSIFICATION_FEATURE_ACCESS } from "constants/subscriptionPermissionFeatures";
import { PermissionBasedUI } from "components/PermissionBasedUI/PermissionBasedUI";

export type RuleValuesType = {
  name: string;
  startDate: string;
  endDate: string;
  transactionsFor: string;
  assigned_category_id: string;
  apply_for_future_txns: boolean;
  apply_for_txns_with_no_category: boolean;
  apply_on_all_past_txns: boolean;
  ruleConditionData: {
    id: string;
    field: string;
    conditionFunction: string;
    conditionValue: string | string[];
  }[];
  priority: RulePriorityType;
  assigned_vendor_id: string;
  classification_ids: string[];
};

const initialRuleConditionData = {
  field: "",
  conditionFunction: "",
  conditionValue: "",
};

export const RuleStepper = ({
  currentStep,
  onStepperClick,
}: {
  onStepperClick: (step: number) => void;
  currentStep: number;
}) => {
  const { isMobile } = useWindowSize();
  const breadcrumbs = [
    { name: "Apply rule", step: 1 },
    { name: "Preview transactions", step: 2 },
  ];

  return (
    <Stepper
      size={isMobile ? "xs" : "small"}
      direction="horizontal"
      className="sm:t-gap-y-1.5"
    >
      {breadcrumbs?.map(({ name, step }) => (
        <Stepper.Step
          step={step}
          key={step}
          isActive={currentStep >= step}
          clickable={step === 1}
          onClick={() => onStepperClick(step)}
        >
          <div className="t-flex t-items-center">
            {name}
            {step === 1 && <ArrowRight color="currentColor" />}
          </div>
        </Stepper.Step>
      ))}
    </Stepper>
  );
};

export const TransactionsPreview = ({
  uuid,
  close,
  onSubmit,
  currentStep,
  setCurrentStep,
  linfoLabel = false,
  submitText,
  modalTitle,
}: {
  uuid: string | undefined;
  currentStep: number;
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  close: () => void;
  onSubmit: ({ ruleId }: { ruleId: string }) => void | Promise<any>;
  linfoLabel?: ReactNode;
  submitText: string;
  modalTitle: string;
}) => {
  const infiniteScrollId = useId();
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();

  const {
    data: transactionData,
    loadNext,
    isLoading,
    isSuccess,
  } = usePaginatedQuery<{
    rule: RulesData;
    transactions: Transactions[];
  }>(useLazyGetRuleTransactionsQuery, "transactions", {
    groupId,
    entityId,
    ruleId: uuid || "",
  });

  const {
    rule,
    transactions = [],
    current_page = 1,
    total_pages = 1,
    total_count,
  } = transactionData || {};

  const { category } = rule || {};
  const { name } = category || {};
  const isEmpty = transactions.length === 0;

  return (
    <Formik
      initialValues={{
        ruleId: uuid || "",
      }}
      onSubmit={onSubmit}
    >
      {({ submitForm, isSubmitting }) => (
        <Modal.Content size="xl" useCustomOverlay>
          <Modal.Header className="sm:t-pl-4">
            <div>
              <Modal.Title>{modalTitle}</Modal.Title>
              <Modal.Subtitle>
                <RuleStepper
                  currentStep={currentStep}
                  onStepperClick={setCurrentStep}
                />
              </Modal.Subtitle>
            </div>
            <Modal.Close />
          </Modal.Header>
          <Async.Root {...{ isEmpty, isLoading, isSuccess }}>
            <Async.Empty>
              <EmptyScreen text="No transactions found!" />
            </Async.Empty>

            <Async.Success>
              <Modal.Body className="t-flex t-gap-6 t-flex-col t-overflow-x-hidden sm:t-px-4">
                <Form
                  className="t-m-0 t-flex t-flex-col t-h-full t-overflow-y-auto t-gap-6"
                  id={infiniteScrollId}
                >
                  {Boolean(linfoLabel) && <>{linfoLabel}</>}
                  <div className="t-flex t-justify-between t-text-subtext t-text-text-60">
                    <span className="t-uppercase">CATEGORY: {name}</span>
                    <span>{total_count} Transactions</span>
                  </div>

                  <InfiniteScroll
                    dataLength={transactions?.length || 0}
                    next={loadNext}
                    hasMore={total_pages > current_page}
                    scrollableTarget={infiniteScrollId}
                    className="t-space-y-5"
                    loader={
                      <div className="t-flex t-justify-center t-items-center t-w-full t-gap-2 t-p-2">
                        <span className="t-flex t-origin-center t-animate-spin">
                          <LoadingIcon />
                        </span>
                        Loading...
                      </div>
                    }
                  >
                    <TransactionsView transactions={transactions} />
                  </InfiniteScroll>
                </Form>
              </Modal.Body>
            </Async.Success>
          </Async.Root>
          <Modal.FooterButtonGroup>
            <Button
              onClick={close}
              disabled={isSubmitting || isLoading}
              type="button"
            >
              Cancel
            </Button>
            <Button
              customType="primary"
              onClick={submitForm}
              isLoading={isSubmitting}
              disabled={isSubmitting || isLoading}
            >
              {submitText}
            </Button>
          </Modal.FooterButtonGroup>
        </Modal.Content>
      )}
    </Formik>
  );
};

export const RuleConsoleItems = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => {
  return (
    <div className="t-flex t-flex-col t-gap-4">
      <span className="t-text-subtext t-text-text-100">{title}</span>
      {children}
    </div>
  );
};

export const TitleAndDuration = () => {
  const { values, setFieldValue } = useFormikContext<RuleValuesType>();
  const { startDate, apply_for_future_txns, apply_on_all_past_txns } = values;
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);

  let durationTitle = `Select Duration`;

  const onlyFutureTxn = apply_for_future_txns && !apply_on_all_past_txns;
  const hasSelection = apply_for_future_txns || apply_on_all_past_txns;

  if (onlyFutureTxn && startDate) {
    durationTitle = `Start from ${dayjs(startDate).format(DISPLAY_DATE)}`;
  }

  if (!onlyFutureTxn && hasSelection) {
    durationTitle = "To all transactions";
  }

  const handleStartFromDateSelect = () => {
    setFieldValue("apply_for_future_txns", true);
    setFieldValue("apply_on_all_past_txns", false);
    setFieldValue("transactionsFor", "allFutureTransactions");
    setIsDropdownOpen(false);
  };

  const handleAllTransactionsSelect = () => {
    setFieldValue("startDate", null);
    setFieldValue("endDate", null);
    setFieldValue("apply_for_future_txns", true);
    setFieldValue("apply_on_all_past_txns", true);
    setFieldValue("transactionsFor", "allTransactions");
    setShowDatePicker(false);
    setIsDropdownOpen(false);
  };

  const startDateTitle = startDate ? dayjs(startDate) : dayjs();

  return (
    <RuleConsoleItems title="Title">
      <div className="t-flex t-gap-4 t-flex-col">
        <div className="t-flex t-gap-4 sm:t-flex-col">
          <div className="t-flex-1">
            <TextInput
              name="name"
              label="Rule name"
              placeholder="Rule#1"
              required
              block
            />
          </div>
          <div className="t-flex t-flex-col t-gap-1.5 t-flex-1 t-relative">
            <Label className="!t-pb-0" required>
              Duration
            </Label>
            <Dropdown.Root
              open={isDropdownOpen}
              onOpenChange={(open) => {
                if (!open && showDatePicker) {
                  return;
                }
                setIsDropdownOpen(open);
              }}
            >
              <Dropdown.Trigger asChild>
                <div className="all:unset t-box-border t-flex t-h-10 t-w-[100%] t-items-center t-justify-between t-rounded t-border t-border-solid t-border-neutral-10 t-bg-surface-lighter-grey t-px-4 t-transition-all t-cursor-pointer">
                  <div className="t-flex t-items-center t-gap-2 t-flex-1 t-min-w-0">
                    <div
                      className={classNames(
                        "t-overflow-hidden t-text-ellipsis t-whitespace-nowrap t-text-body-sm",
                        {
                          "t-text-text-100": hasSelection,
                          "t-text-text-30": !hasSelection,
                        }
                      )}
                    >
                      {durationTitle}
                    </div>
                  </div>
                  <div
                    className={classNames(
                      "t-transition-transform t-duration-200 t-text-text-100",
                      {
                        "-t-rotate-180": isDropdownOpen,
                      }
                    )}
                  >
                    <ArrowDown strokeWidth="1.4" color="currentColor" />
                  </div>
                </div>
              </Dropdown.Trigger>
              <Dropdown.Portal>
                <Dropdown.Content
                  align="start"
                  sideOffset={4}
                  onInteractOutside={(e) => {
                    const target = e.target as HTMLElement;
                    if (target.closest('[id^="date-portal-"]')) {
                      e.preventDefault();
                    }
                  }}
                >
                  <Dropdown.Item
                    asChild
                    className={classNames({
                      "t-bg-purple-0": onlyFutureTxn,
                    })}
                  >
                    <div className="t-flex t-justify-between t-gap-1 t-w-full">
                      <button
                        type="button"
                        className="all:unset t-flex t-flex-col t-gap-1 t-flex-1 t-cursor-pointer"
                        onClick={handleStartFromDateSelect}
                      >
                        <div className="t-text-body-sm t-text-text-100">
                          Start from {startDateTitle.format(DISPLAY_DATE)}
                        </div>
                        <div className="t-text-subtext-sm t-text-text-30">
                          From the selected date to future transactions
                        </div>
                      </button>
                      <div className="t-flex t-items-center">
                        <Field name="startDate">
                          {({ field }: FieldProps) => (
                            <DateInput
                              {...field}
                              label="Start Date"
                              name="startDate"
                              value={startDate}
                              customDatePickerInputTrigger={
                                <div>
                                  <Button customType="ghost_icon" size="xsmall">
                                    <EditPencil className="t-size-4 t-text-purple" />
                                  </Button>
                                </div>
                              }
                              portalId="calendar-button"
                              onCalendarOpen={() => setShowDatePicker(true)}
                              onCalendarClose={() => setShowDatePicker(false)}
                              onDateChange={(date) => {
                                const formattedDate =
                                  dayjs(date).format(MM_DD_YYYY);
                                setFieldValue("startDate", formattedDate);
                                setShowDatePicker(false);
                                handleStartFromDateSelect();
                              }}
                            />
                          )}
                        </Field>
                      </div>
                    </div>
                  </Dropdown.Item>
                  <Dropdown.Item
                    onSelect={handleAllTransactionsSelect}
                    className={classNames({
                      "t-bg-purple-0":
                        apply_for_future_txns && apply_on_all_past_txns,
                    })}
                  >
                    <div className="t-flex t-flex-col t-gap-1">
                      <div className="t-text-body-sm t-text-text-100">
                        To all transactions
                      </div>
                      <div className="t-text-subtext-sm t-text-text-30">
                        Includes all the past and future transactions
                      </div>
                    </div>
                  </Dropdown.Item>
                </Dropdown.Content>
              </Dropdown.Portal>
            </Dropdown.Root>
          </div>
          <div className="t-flex-1">
            <Combobox
              withForm
              name="priority"
              label="Priority"
              placeholder="Select priority"
              options={RULE_PRIORITY_OPTIONS}
              menuPortalTarget={document.body}
              block
              required
              value={
                RULE_PRIORITY_OPTIONS.find(
                  (option) => option.value === values.priority
                ) || null
              }
            />
          </div>
        </div>
      </div>
    </RuleConsoleItems>
  );
};

const ConditionWrapper = ({
  itemId,
  children,
}: {
  children: ({ field }: { field: string | undefined }) => ReactNode;
  itemId: string;
}) => {
  const { values } = useFormikContext<RuleValuesType>();
  const { ruleConditionData = [] } = values || {};
  const { field } = ruleConditionData.find(({ id }) => id === itemId) || {};

  return <>{children({ field })}</>;
};

export const ConditionValue = ({
  index,
  field,
  defaultValue,
}: {
  index: number;
  field: string | undefined;
  defaultValue?: PropsValue<OptionData>;
}) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();

  const { data: conditions = [] } = useGetAllConditionsQuery(
    {
      groupId,
      entityId,
    },
    { skip: !groupId || !entityId }
  );

  const { values } = useFormikContext<{
    ruleConditionData: RuleConditionRow[];
  }>();

  const conditionFunctions = conditions.find(
    ({ uuid, name }) => field === uuid || name === field
  );
  const { possible_values = [], value_supported } = conditionFunctions || {};

  const hasAmountRangeError = getInvalidRuleAmountRangeRows(
    conditions,
    values.ruleConditionData
  ).includes(index);

  const conditionValueOptions = possible_values.map(({ name, uuid }) => ({
    label: <span className="t-lowercase">{name}</span>,
    value: name,
  }));

  switch (value_supported) {
    case "SOURCE": {
      return (
        <SourceValue
          index={index}
          label={index === 0 ? "Value" : ""}
          name={`ruleConditionData[${index}].conditionValue`}
        />
      );
    }

    case "VENDOR": {
      return (
        <VendorValue
          index={index}
          label={index === 0 ? "Value" : ""}
          name={`ruleConditionData[${index}].conditionValue`}
        />
      );
    }
    case "CHOICE": {
      return (
        <Combobox
          aria-label={`${formatOrdinals(index + 1)} Value`}
          key={field}
          label={index === 0 ? "Value" : ""}
          name={`ruleConditionData[${index}].conditionValue`}
          required
          withForm
          hideError
          placeholder="Select"
          menuPortalTarget={document.body}
          options={conditionValueOptions}
          isClearable={false}
          block
          defaultValue={Boolean(defaultValue) ? defaultValue : undefined}
        />
      );
    }

    case "DECIMAL": {
      return (
        <PriceInput
          aria-label={`${formatOrdinals(index + 1)} Value`}
          label={index === 0 ? "Value" : ""}
          name={`ruleConditionData[${index}].conditionValue`}
          required
          placeholder="Enter amount"
          hideError={!hasAmountRangeError}
          error={hasAmountRangeError ? AMOUNT_RANGE_ERROR : undefined}
          showErrorOnceTouched
        />
      );
    }

    case "TEXT": {
      return (
        <TextInput
          aria-label={`${formatOrdinals(index + 1)} Value`}
          label={index === 0 ? "Value" : ""}
          name={`ruleConditionData[${index}].conditionValue`}
          required
          placeholder="Enter value"
          hideError
          showErrorOnceTouched
        />
      );
    }

    default:
      return (
        <TextInput
          aria-label={`${formatOrdinals(index + 1)} Value`}
          disabled
          label={index === 0 ? "Value" : ""}
          required
          placeholder="Enter value"
          name={`ruleConditionData[${index}].conditionValue`}
          hideError
          showErrorOnceTouched
        />
      );
  }
};

export const ConditionFunction = ({
  index,
  field,
  defaultValue,
}: {
  index: number;
  field: string | undefined;
  defaultValue?: PropsValue<OptionData>;
}) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();

  const { values } = useFormikContext<{
    ruleConditionData: RuleConditionRow[];
  }>();

  const { data: conditions = [], isLoading } = useGetAllConditionsQuery(
    {
      groupId,
      entityId,
    },
    { skip: !groupId || !entityId }
  );

  const conditionFunctions = conditions.find(
    ({ uuid, name }) => field === uuid || name === field
  );

  const available_operators = getAvailableRuleOperators({
    condition: conditionFunctions,
    rows: values.ruleConditionData,
    rowIndex: index,
  });

  const conditionFunctionOptions = available_operators.map(
    ({ name, uuid }) => ({
      label: <span className="t-lowercase">{name}</span>,
      value: uuid,
    })
  );

  return (
    <Combobox
      aria-label={`${formatOrdinals(index + 1)} Function`}
      key={field}
      isDisabled={isLoading || !Boolean(available_operators.length)}
      placeholder="Select"
      menuPortalTarget={document.body}
      name={`ruleConditionData[${index}].conditionFunction`}
      withForm
      label={index === 0 ? "Function" : ""}
      required
      options={conditionFunctionOptions}
      isClearable={false}
      block
      hideError
      styles={{
        menuPortal: (base) => ({
          ...base,
          width: 250,
        }),
      }}
      defaultValue={Boolean(defaultValue) ? defaultValue : undefined}
    />
  );
};

const Conditions = () => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { values, setFieldValue } = useFormikContext<RuleValuesType>();
  const { ruleConditionData = [] } = values || {};

  const {
    data: conditions = [],
    isLoading,
    isSuccess,
  } = useGetAllConditionsQuery(
    {
      groupId,
      entityId,
    },
    { skip: !groupId || !entityId }
  );

  const fieldOptionsByRow = useMemo(
    () =>
      ruleConditionData.map((_, rowIndex) =>
        getAvailableRuleFields({
          conditions,
          rows: ruleConditionData,
          rowIndex,
        })
      ),
    [conditions, ruleConditionData]
  );

  const onFieldChange = (index: number) => {
    setFieldValue(`ruleConditionData[${index}].conditionFunction`, "");
    setFieldValue(`ruleConditionData[${index}].conditionValue`, "");
  };

  const addNewCondition = () => {
    if (ruleConditionData.length < MAX_RULE_CONDITIONS) {
      const newValues = [
        ...values.ruleConditionData,
        {
          id: randomBytes(10).toString("hex"),
          ...initialRuleConditionData,
        },
      ];
      setFieldValue("ruleConditionData", newValues);
    }
  };

  const onRemove = (conditionId: string) => {
    if (ruleConditionData.length > 1) {
      const newValues = ruleConditionData.filter(({ id }) => {
        return id !== conditionId;
      });

      setFieldValue("ruleConditionData", newValues);
    }
  };

  if (isSuccess) {
    return (
      <RuleConsoleItems title="Define Conditions">
        <AnimatePresence>
          {ruleConditionData.map(({ id, conditionFunction, field }, i) => {
            const defaultConditionField = conditions.find(
              ({ uuid }) => uuid === field
            );

            const defaultConditionFunction =
              defaultConditionField?.available_operators.find(
                ({ uuid }) => uuid === conditionFunction
              );

            return (
              <motion.div
                className="t-flex t-gap-4"
                key={id}
                layout
                transition={{ duration: 0.1, ease: "easeOut" }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -500, opacity: 0 }}
              >
                <ConditionWrapper itemId={id}>
                  {({ field }) => (
                    <div className="t-grid t-grid-cols-3 t-gap-4 t-w-full sm:t-grid-cols-1 sm:t-gap-2">
                      <Combobox
                        label={i === 0 ? "Field" : ""}
                        aria-label={`${formatOrdinals(i + 1)} Field`}
                        isDisabled={isLoading}
                        placeholder="Select"
                        menuPortalTarget={document.body}
                        name={`ruleConditionData[${i}].field`}
                        withForm
                        options={fieldOptionsByRow[i]}
                        isClearable={false}
                        block
                        required
                        onChange={() => onFieldChange(i)}
                        {...{
                          defaultValue:
                            Boolean(defaultConditionField?.uuid) && i === 0
                              ? {
                                  label: defaultConditionField?.name || "",
                                  value: defaultConditionField?.uuid || "",
                                }
                              : undefined,
                        }}
                      />
                      <ConditionFunction
                        index={i}
                        field={field}
                        {...{
                          defaultValue: Boolean(defaultConditionFunction?.uuid)
                            ? {
                                label: defaultConditionFunction?.name || "",
                                value: defaultConditionFunction?.uuid || "",
                              }
                            : undefined,
                        }}
                      />
                      <ConditionValue index={i} field={field} />
                    </div>
                  )}
                </ConditionWrapper>
                <div className="t-self-end">
                  <Button
                    customType="ghost_icon"
                    onClick={() => onRemove(id)}
                    disabled={ruleConditionData.length === 1}
                    type="button"
                  >
                    <Cross />
                  </Button>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>

        <div>
          <Button
            size="small"
            onClick={addNewCondition}
            disabled={ruleConditionData.length >= MAX_RULE_CONDITIONS}
            type="button"
            customType="link"
          >
            Add a condition
          </Button>
        </div>
      </RuleConsoleItems>
    );
  }

  return null;
};

export const Actions = ({
  assigned_category_id,
  assigned_vendor_id,
}: {
  assigned_category_id: string;
  assigned_vendor_id: string;
}) => {
  const { uuid: groupId } = useCurrentGroup();
  const { setFieldValue, values } = useFormikContext<RuleValuesType>();
  const { chartOfAccounts = [], isLoading } = useChartOfAccounts({
    hiddenCategoryTypes: ["BANK_ACCOUNT"],
  });
  const entityId = useCurrentEntityId();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showClassificationSelector, setShowClassificationSelector] =
    useState(false);
  const addNewGroupModal = useModal();
  const addNewClassModal = useModal();
  const [addClassToGroup, setAddClassToGroup] = useState<string | null>(null);

  const { data: allClassifications = [] } = useGetAllClassificationQuery(
    { entityId },
    { skip: !entityId }
  );

  const assignedClasses: ClassificationGroup[] = allClassifications
    .map((group) => ({
      ...group,
      classes: group.classes.filter((cls) =>
        values.classification_ids.includes(cls.uuid)
      ),
    }))
    .filter((group) => group.classes.length > 0);

  const hasSelectedValues = values.classification_ids.length > 0;
  const selectedCount = values.classification_ids.length;

  const handleCheckboxChange = (classUuid: string, groupUuid: string) => {
    const isSelected = values.classification_ids.includes(classUuid);
    if (isSelected) {
      handleRemoveClass(classUuid);
    } else {
      const existingClassInGroup = allClassifications
        .find((group) => group.uuid === groupUuid)
        ?.classes.find((cls) => values.classification_ids.includes(cls.uuid));

      handleAssignClass(classUuid, groupUuid, existingClassInGroup?.uuid);
    }
  };

  const handleAddAnotherClick = () => {
    setShowClassificationSelector(true);
    setIsDropdownOpen(true);
  };

  const { data, isLoading: isMerchantLoading } = useGetMerchantByIdQuery(
    { groupId: groupId!, merchantId: assigned_vendor_id || "" },
    { skip: !groupId || !assigned_vendor_id }
  );
  const { name = "" } = data || {};

  const defaultAssignedCategory = flattenTypes({
    accounts: chartOfAccounts,
  }).find(({ uuid }) => uuid === assigned_category_id);

  const handleAssignClass = (
    classUuid: string,
    groupUuid: string,
    existingClassToRemove?: string
  ) => {
    if (values.classification_ids.includes(classUuid)) {
      return;
    }

    let newClassifications = [...values.classification_ids];

    if (existingClassToRemove) {
      newClassifications = newClassifications.filter(
        (id) => id !== existingClassToRemove
      );
    }
    newClassifications = [...newClassifications, classUuid];
    setFieldValue("classification_ids", newClassifications);
  };

  const handleRemoveClass = (classUuid: string) => {
    const newClassifications = values.classification_ids.filter(
      (id) => id !== classUuid
    );
    setFieldValue("classification_ids", newClassifications);
  };

  if (isLoading) {
    return null;
  }

  return (
    <RuleConsoleItems title="Actions">
      <div className="t-flex t-flex-col t-gap-4">
        <div className="t-flex t-flex-row t-gap-4 t-w-full sm:t-flex-col">
          <div className="t-w-full">
            <Combobox
              label="Assign Category"
              menuPlacement="top"
              isDisabled={isLoading}
              placeholder="Select"
              menuPortalTarget={document.body}
              withForm
              name="assigned_category_id"
              options={coaOptions(chartOfAccounts)}
              filterOption={(v, i) =>
                v.data.data
                  ?.toLocaleLowerCase()
                  .includes(i.toLocaleLowerCase()) || false
              }
              value={
                assigned_category_id
                  ? {
                      label: (
                        <CategoryIndent
                          label={
                            <CategoryLabel category={defaultAssignedCategory} />
                          }
                          indentLevel={0}
                        />
                      ),
                      value: defaultAssignedCategory?.uuid || "",
                    }
                  : null
              }
            />
          </div>
          <div className="t-w-full">
            <MerchantSelector
              isLoading={isMerchantLoading}
              isDisabled={isMerchantLoading}
              value={
                assigned_vendor_id
                  ? {
                      value: assigned_vendor_id,
                      label: name,
                    }
                  : null
              }
              withForm
              name="assigned_vendor_id"
              label="Assign Vendor"
              placeholder="Select"
              menuPortalTarget={document.body}
              showAction={false}
            />
          </div>
          <div className="t-w-full">
            <ClassificationAssignment
              entityId={entityId}
              assignedClassificationIds={values.classification_ids}
              onAssignClass={handleAssignClass}
              onRemoveClass={handleRemoveClass}
              label="Assign Classification"
              blockedUI={
                <Combobox
                  label={
                    <span className="t-flex t-items-center t-gap-1">
                      Assign Classification{" "}
                      <DiamondPurple height={12} width={12} />
                    </span>
                  }
                  placeholder="Select"
                  menuPortalTarget={document.body}
                  isDisabled
                  value={[]}
                  options={[]}
                />
              }
            />
          </div>
        </div>
        <div className="t-text-body-sm t-text-text-30">
          Note: Rules do not update transactions that are manually categorised,
          manually confirmed, or part of a locked period.
        </div>
      </div>
    </RuleConsoleItems>
  );
};

const RuleConsole = ({
  close,
  onSubmit,
  currentStep,
  setCurrentStep,
  savedFormValues,
}: {
  currentStep: number;
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  close: () => void;
  onSubmit: (
    values: RuleValuesType,
    formikHelpers: FormikHelpers<RuleValuesType>
  ) => void | Promise<any>;
  savedFormValues: RuleValuesType | null;
}) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();

  const { data: allConditions } = useGetAllConditionsQuery(
    { groupId, entityId },
    { skip: !groupId || !entityId }
  );

  const { categoryId, merchantIds } = useSelector(
    (state: RootState) => state.ruleEngine
  );

  const { data: prefilledRuleData, isSuccess } = useGetPrefilledRuleQuery(
    {
      groupId,
      entityId,
      categoryId,
      merchantIds,
    },
    { skip: !groupId || !entityId || !categoryId || !merchantIds }
  );

  const { conditions = [] } = prefilledRuleData || {};

  const ruleConditionData = useMemo(() => {
    return conditions.length > 0
      ? conditions.map(({ operand, operator, values }) => {
          return {
            id: randomBytes(10).toString("hex"),
            ...{
              field: operand?.uuid || "",
              conditionFunction: operator?.uuid || "",
              conditionValue: values || "",
            },
          };
        })
      : [
          {
            id: randomBytes(10).toString("hex"),
            ...{
              field: "",
              conditionFunction: "",
              conditionValue: "",
            },
          },
        ];
  }, [isSuccess]);

  return (
    <Formik
      initialValues={
        savedFormValues || {
          name: "",
          startDate: "",
          endDate: "",
          transactionsFor: "",
          assigned_category_id: categoryId || "",
          apply_for_future_txns: false,
          apply_on_all_past_txns: false,
          apply_for_txns_with_no_category: true,
          ruleConditionData,
          priority: RULE_PRIORITY.PRIORITY_2 as RulePriorityType,
          assigned_vendor_id: "",
          classification_ids: [] as string[],
        }
      }
      validateOnChange
      validationSchema={categorisationRuleSchema(allConditions)}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({
        submitForm,
        validateForm,
        isSubmitting,
        isValid,
        values: {
          assigned_category_id,
          assigned_vendor_id,
          classification_ids,
          ...restValue
        },
      }) => {
        const hasAtLeastOneAction =
          Boolean(assigned_category_id) ||
          Boolean(assigned_vendor_id) ||
          (classification_ids && classification_ids.length > 0);

        const isButtonDisabled =
          isSubmitting || !hasAtLeastOneAction || !isValid;
        return (
          <Modal.Content size="xl" useCustomOverlay>
            <Modal.Header className="sm:t-pl-4">
              <div>
                <Modal.Title>Create new rule</Modal.Title>
                <Modal.Subtitle>
                  <RuleStepper
                    currentStep={currentStep}
                    onStepperClick={setCurrentStep}
                  />
                </Modal.Subtitle>
              </div>
              <Modal.Close />
            </Modal.Header>
            <Modal.Body className="sm:t-px-4">
              <Form>
                <div className="t-flex t-flex-col t-gap-6">
                  <TitleAndDuration />
                  <Divider />
                  <Conditions />
                  <Actions
                    assigned_category_id={assigned_category_id}
                    assigned_vendor_id={assigned_vendor_id}
                  />
                </div>
              </Form>
            </Modal.Body>
            <Modal.FooterButtonGroup>
              <Button onClick={close} disabled={isSubmitting} type="button">
                Cancel
              </Button>
              <ConditionalToolTip
                condition={
                  !hasAtLeastOneAction && "Please select at least one of Action"
                }
              >
                <div>
                  <Button
                    customType="primary"
                    onClick={submitForm}
                    isLoading={isSubmitting}
                    disabled={isButtonDisabled}
                  >
                    Create & Preview transactions
                  </Button>
                </div>
              </ConditionalToolTip>
            </Modal.FooterButtonGroup>
          </Modal.Content>
        );
      }}
    </Formik>
  );
};

export const CreateRule = ({ close, isOpen }: ModalProps) => {
  const { alertToast, successToast } = useToast();
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const [currentStep, setCurrentStep] = useState(1);
  const [savedFormValues, setSavedFormValues] = useState<RuleValuesType | null>(
    null
  );
  const [applyRule] = useApplyRuleMutation();
  const [createRule, { data }] = useCreateRuleMutation();

  const { uuid } = data || {};

  const onClose = () => {
    setCurrentStep(1);
    setSavedFormValues(null);
    close();
  };

  const onCreate = async (values: RuleValuesType) => {
    const {
      endDate,
      startDate,
      ruleConditionData,
      classification_ids,
      assigned_vendor_id,
      ...rest
    } = values;

    try {
      await createRule({
        entityId,
        groupId,
        rule_condition_data: ruleConditionData.map(
          ({ conditionFunction, conditionValue, field }) => ({
            value: conditionValue,
            operator_id: conditionFunction,
            operand_id: field,
          })
        ),
        rule_end_date: endDate ? dayjs(endDate).format(YYYY_MM_DD) : undefined,
        rule_start_date: startDate
          ? dayjs(startDate).format(YYYY_MM_DD)
          : undefined,
        assigned_classfication_ids: classification_ids,
        assigned_merchant_id: assigned_vendor_id,

        ...rest,
      }).unwrap();
      setSavedFormValues(values);
      setCurrentStep(2);
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const onActive = async ({ ruleId }: { ruleId: string }) => {
    try {
      await applyRule({ entityId, groupId, ruleId }).unwrap();
      onClose();
      successToast({ message: "Rule created" });
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={onClose} modal={false}>
      {currentStep === 1 ? (
        <RuleConsole
          onSubmit={onCreate}
          close={close}
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
          savedFormValues={savedFormValues}
        />
      ) : (
        <TransactionsPreview
          uuid={uuid}
          onSubmit={onActive}
          close={onClose}
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
          submitText="Apply"
          modalTitle="Create new rule"
        />
      )}
    </Modal.Root>
  );
};
